import { Dialog } from '@headlessui/react';
import { countries } from 'countries-list';
import React from 'react';
import { useForm } from 'react-hook-form';
import { IoCloseCircleOutline } from 'react-icons/io5';

import Button from '@/components/Primitive/Button/Button';
import SelectField from '@/components/Primitive/SelectField/SelectField';
import TextField from '@/components/Primitive/TextField/TextField';

interface IWaitlistForm {
  open: boolean;
  onClose: () => void;
  error: string;
  isSuccess: boolean;
  isLoading: boolean;
  onSubmit: (data: WaitlistFormData) => Promise<boolean>;
}

export type WaitlistFormData = {
  email: string;
  firstName: string;
  lastName: string;
  country: string;
};

const WaitlistForm = ({
  onClose,
  open,
  onSubmit,
  isLoading,
  isSuccess,
  error,
}: IWaitlistForm) => {
  const { register, handleSubmit } = useForm<WaitlistFormData>();
  const _onSubmit = async (formData: WaitlistFormData) => {
    await onSubmit(formData);
  };

  const countryOptions = Object.values(countries).map((country) => ({
    value: country.name,
    label: country.name,
  }));

  return (
    <Dialog className="relative z-50" open={open} onClose={onClose}>
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="container-sm fixed inset-0 flex items-center justify-center">
        <Dialog.Panel className="relative  w-full rounded-lg bg-blueAlt-900 p-4">
          <button onClick={onClose} className="absolute -right-4 -top-4">
            <IoCloseCircleOutline className="h-10 w-10 text-cream" />
          </button>
          <div className="max-h-[95vh] overflow-y-auto">
            {!isSuccess && (
              <form onSubmit={handleSubmit(_onSubmit)} id="waitlist-form">
                <div className="space-y-3">
                  <h2 className="text-2xl font-bold uppercase text-cream md:text-3xl">
                    Sign up to hear when PortalOne is in your region!
                  </h2>
                  <div className="sr-only">
                    <span className="asterisk">*</span> indicates required
                  </div>

                  <TextField
                    type="email"
                    id="email"
                    required
                    label="Your email address"
                    {...register('email')}
                  />

                  <TextField
                    type="text"
                    id="firstName"
                    required
                    label="First Name"
                    {...register('firstName')}
                  />

                  <TextField
                    type="text"
                    id="lastName"
                    required
                    label="Last Name"
                    {...register('lastName')}
                  />

                  <SelectField
                    id="country"
                    required
                    options={[
                      { label: 'What country do you live in?', value: '' },
                      ...countryOptions,
                    ]}
                    {...register('country')}
                  />

                  {error && <p className="mr-0 text-red-500">{error}</p>}
                  {isSuccess && (
                    <p className="mr-0 text-green-500">
                      Your message has been sent successfully.
                    </p>
                  )}

                  <div className="py-2">
                    <Button
                      loading={isLoading}
                      type="submit"
                      className="uppercase"
                    >
                      Subscribe
                    </Button>
                  </div>
                </div>
              </form>
            )}
            {isSuccess && (
              <div className="flex w-full flex-col items-center justify-center py-12 text-center">
                <h2 className="font-dharma text-5xl font-black uppercase italic leading-none text-cream">
                  Success!
                </h2>
                <p className="text-lg text-cream">
                  You’ll hear from us when we’re available in your region.{' '}
                </p>
              </div>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default WaitlistForm;
